import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { PageHeader, ActionButton } from "../components/Layout";
import { Save, Search, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function ShipmentNewPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [orderPieces, setOrderPieces] = useState({}); // {orderId: [pieces]}
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    shipment_number: "", customer_name: "", destination: "",
    shipment_date: todayISO(), vehicle_info: "", container_ref: "", notes: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders");
        // Only show orders that have at least one shippable piece
        setOrders(data.filter(o => (o.ready || 0) > 0));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  // When user selects/deselects an order, fetch its pieces (cache)
  useEffect(() => {
    const idsToFetch = [...selectedOrders].filter(id => !orderPieces[id]);
    if (idsToFetch.length === 0) return;
    (async () => {
      for (const id of idsToFetch) {
        try {
          const { data } = await api.get(`/orders/${id}`);
          const eligible = data.pieces.filter(p => p.status === "HAZIR" && !p.shipment_id);
          setOrderPieces(prev => ({ ...prev, [id]: eligible }));
          // Also autofill customer/destination from first order if empty
          setForm(f => ({
            ...f,
            customer_name: f.customer_name || data.customer_name,
            destination: f.destination || data.destination,
          }));
        } catch (e) { /* silent */ }
      }
    })();
  }, [selectedOrders]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    if (!s) return orders;
    return orders.filter(o =>
      o.order_number.toLowerCase().includes(s) ||
      (o.reference || "").toLowerCase().includes(s) ||
      (o.customer_name || "").toLowerCase().includes(s)
    );
  }, [orders, q]);

  const toggleOrder = (id) => {
    const s = new Set(selectedOrders);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedOrders(s);
  };
  const toggleAll = () => {
    if (selectedOrders.size === filtered.length) setSelectedOrders(new Set());
    else setSelectedOrders(new Set(filtered.map(o => o.id)));
  };

  const totalPieces = useMemo(() => {
    let n = 0;
    for (const id of selectedOrders) n += (orderPieces[id] || []).length;
    return n;
  }, [selectedOrders, orderPieces]);

  const submit = async (e) => {
    e.preventDefault();
    if (selectedOrders.size === 0) return toast.error("En az bir sipariş seçin");
    // Collect all piece ids
    const allPieceIds = [];
    for (const id of selectedOrders) {
      (orderPieces[id] || []).forEach(p => allPieceIds.push(p.id));
    }
    if (allPieceIds.length === 0) return toast.error("Seçili siparişlerde hazır parça yok");

    setBusy(true);
    try {
      const { data } = await api.post("/shipments", { ...form, piece_ids: allPieceIds });
      toast.success(`Sevkiyat oluşturuldu — ${data.shipment_number || form.shipment_number}`);
      navigate(`/sevkiyatlar/${data.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader title="Yeni Sevkiyat" subtitle="Birden fazla sipariş seçebilirsiniz. Hazır parçalar otomatik dahil edilir." />

      <form onSubmit={submit} className="space-y-6" data-testid="new-shipment-form">
        <section className="card-industrial p-6">
          <h2 className="font-display font-bold uppercase tracking-wide text-sm text-slate-500 mb-4">Sevkiyat Bilgileri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Sevkiyat No" required>
              <input data-testid="ship-number" required value={form.shipment_number} onChange={(e) => setForm({ ...form, shipment_number: e.target.value })} className={inputCls + " font-mono"} placeholder="SEV-2026-0001" />
            </Field>
            <Field label="Müşteri" required>
              <input data-testid="ship-customer" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Varış" required>
              <input data-testid="ship-destination" required value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Sevkiyat Tarihi" required>
              <input data-testid="ship-date" required type="date" value={form.shipment_date} onChange={(e) => setForm({ ...form, shipment_date: e.target.value })} className={inputCls + " font-mono"} />
            </Field>
            <Field label="Araç / Plaka">
              <input value={form.vehicle_info} onChange={(e) => setForm({ ...form, vehicle_info: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Konteyner Referansı">
              <input value={form.container_ref} onChange={(e) => setForm({ ...form, container_ref: e.target.value })} className={inputCls + " font-mono"} />
            </Field>
            <Field label="Notlar" className="md:col-span-3">
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
            </Field>
          </div>
        </section>

        <section className="card-industrial p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="font-display font-bold uppercase tracking-wide text-sm text-slate-500">Siparişleri Seç</h2>
            <div className="text-sm">
              Seçili: <b className="font-mono text-blue-700" data-testid="ship-orders-count">{selectedOrders.size}</b> sipariş · <b className="font-mono text-emerald-700" data-testid="ship-pieces-count">{totalPieces}</b> parça
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sipariş / Müşteri / Referans ara..."
                className="w-full pl-10 pr-4 py-2 border-2 border-slate-300 focus:border-blue-700 outline-none"/>
            </div>
            <button type="button" onClick={toggleAll} className="px-3 py-2 border-2 border-slate-800 hover:bg-slate-900 hover:text-white font-bold text-xs uppercase">
              {selectedOrders.size === filtered.length ? "Tüm Seçimi Kaldır" : "Tümünü Seç"}
            </button>
          </div>

          <div className="border-2 border-slate-300 max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr className="text-xs uppercase tracking-widest text-slate-600 border-b-2 border-slate-300">
                  <th className="w-12 px-3 py-2"></th>
                  <th className="text-left px-3 py-2">Sipariş</th>
                  <th className="text-left px-3 py-2">Müşteri</th>
                  <th className="text-left px-3 py-2">Referans</th>
                  <th className="text-left px-3 py-2">Ölçü</th>
                  <th className="text-left px-3 py-2">Kumaş</th>
                  <th className="text-right px-3 py-2">Hazır</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Hazır sipariş yok.</td></tr>}
                {filtered.map((o, i) => {
                  const sel = selectedOrders.has(o.id);
                  const readyRemaining = o.ready || 0;
                  return (
                    <tr key={o.id} onClick={() => toggleOrder(o.id)}
                      data-testid={`ship-order-row-${o.order_number}`}
                      className={`border-b border-slate-200 cursor-pointer ${sel ? "bg-blue-50" : (i % 2 === 0 ? "bg-white" : "bg-slate-50")} hover:bg-blue-50`}>
                      <td className="px-3 py-2 text-center">
                        {sel ? <CheckSquare size={18} className="text-blue-700 mx-auto"/> : <Square size={18} className="text-slate-400 mx-auto"/>}
                      </td>
                      <td className="px-3 py-2 font-mono font-bold text-blue-700">{o.order_number}</td>
                      <td className="px-3 py-2 font-semibold">{o.customer_name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">{o.reference || "—"}</td>
                      <td className="px-3 py-2 font-mono">{o.size || "—"}</td>
                      <td className="px-3 py-2 text-xs">{o.fabric || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700 tabular-nums">{readyRemaining}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <div className="flex justify-end sticky bottom-4 z-10 p-4 bg-slate-900 text-white shadow-lg">
          <div className="flex-1 text-sm">
            <b>{selectedOrders.size}</b> sipariş, <b>{totalPieces}</b> parça dahil edilecek
          </div>
          <ActionButton type="submit" disabled={busy || selectedOrders.size === 0} data-testid="submit-shipment-btn">
            <Save size={18} /> {busy ? "Oluşturuluyor..." : "Sevkiyat Oluştur"}
          </ActionButton>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full px-3 py-2 border border-slate-400 bg-white rounded-none focus:border-blue-700 focus:outline-none";

function Field({ label, children, className, required }) {
  return (
    <label className={"block " + (className || "")}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">
        {label}{required && <span className="text-red-600"> *</span>}
      </div>
      {children}
    </label>
  );
}
